import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import type { NeverAuthenticated } from "@/types";
import { formatSnapshotFreshness } from "@/utils/snapshotFreshness.ts";

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

// Rendered only when a snapshot value exists — the parent hides the tile when
// never-authenticated is unmeasured, so there is no placeholder state here.
export default function NeverAuthenticatedTile({
  data,
  asOf,
  onClick,
}: {
  readonly data: NeverAuthenticated;
  readonly asOf?: string | null;
  readonly onClick: () => void;
}) {
  const theme = useTheme();
  const freshness = formatSnapshotFreshness(asOf);
  // One hover target: the whole tile. The snapshot time rides along as a second
  // line in this tooltip rather than a separate info icon with its own tooltip.
  return (
    <Tooltip
      title={
        <>
          Public IPs you queried that never went on to authenticate.
          <Box component="span" sx={{ display: "block", mt: 0.5, opacity: 0.8 }}>
            {freshness ? `Snapshot · ${freshness}` : "From the latest snapshot."}
          </Box>
        </>
      }
      arrow
    >
      <Box onClick={onClick} sx={{ cursor: "pointer" }}>
        <Typography variant="overline" sx={{ color: theme.palette.text.secondary, fontSize: "0.625rem", letterSpacing: "0.08em" }}>
          Never authenticated
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", fontSize: "1.75rem" }}>
          {formatCompact(data.count)}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mt: 0.25 }}>
          {Math.round(data.pct)}% of queried IPs
        </Typography>
      </Box>
    </Tooltip>
  );
}
